import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Input, InputAdornment } from 'tea-component';
import { useIntl } from 'umi';

import { Operation } from '@dbl/core/filter';

import { isValidNumberInput, toInputDisplayValue } from './utils';
import type { INumberOperationValueInputProps } from './types';

import './index.scss';

const BLOCK = 'dbl-num-op-value';

const RANGE_PLACEHOLDER_MESSAGE_ID = 'dbl_sortable_drag_input_number';
const SINGLE_PLACEHOLDER_MESSAGE_ID =
  'dbl_sortable_drag_input_number_placeholder';

const NumberOperationValueInput: React.FC<INumberOperationValueInputProps> = ({
  operation,
  columnValue = [],
  onChange,
  isPercent = false,
  rangePlaceholder,
  singlePlaceholder,
  className,
}) => {
  const { formatMessage } = useIntl();

  const resolvedRangePlaceholder =
    rangePlaceholder ??
    formatMessage({ id: RANGE_PLACEHOLDER_MESSAGE_ID });
  const resolvedSinglePlaceholder =
    singlePlaceholder ??
    formatMessage({ id: SINGLE_PLACEHOLDER_MESSAGE_ID });

  const handleChange = useCallback(
    (number: string, newValue: (string | number)[]) => {
      if (isValidNumberInput(number)) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const handleMouseDown = (event: React.MouseEvent): void => {
    event.stopPropagation();
  };

  const fieldClassName = classNames(`${BLOCK}__field`, {
    [`${BLOCK}__field--pct`]: isPercent,
    [`${BLOCK}__field--between`]: operation === Operation.Between,
  });

  if (operation === Operation.Between) {
    const startRaw = columnValue[0];
    const endRaw = columnValue[1];
    const start = toInputDisplayValue(startRaw);
    const end = toInputDisplayValue(endRaw);

    return (
      <div className={classNames(`${BLOCK}__range`, className)}>
        <InputAdornment
          className={classNames(fieldClassName, `${BLOCK}__field--left`)}
          after={isPercent ? '%' : undefined}
        >
          <Input
            type="number"
            className={`${BLOCK}__input`}
            value={start}
            onChange={number => {
              handleChange(
                number,
                !number && !endRaw ? [] : [number, endRaw ?? ''],
              );
            }}
            placeholder={resolvedRangePlaceholder}
            onMouseDown={handleMouseDown}
          />
        </InputAdornment>
        <InputAdornment
          className={fieldClassName}
          after={isPercent ? '%' : undefined}
        >
          <Input
            type="number"
            className={`${BLOCK}__input`}
            value={end}
            onChange={number => {
              handleChange(
                number,
                !number && !startRaw ? [] : [startRaw ?? '', number],
              );
            }}
            placeholder={resolvedRangePlaceholder}
            onMouseDown={handleMouseDown}
          />
        </InputAdornment>
      </div>
    );
  }

  if (operation === Operation.NotNul || operation === Operation.Null) {
    return null;
  }

  return (
    <InputAdornment
      className={classNames(fieldClassName, className)}
      after={isPercent ? '%' : undefined}
    >
      <Input
        type="number"
        value={toInputDisplayValue(columnValue[0])}
        onChange={number => {
          handleChange(number, number ? [number] : []);
        }}
        placeholder={resolvedSinglePlaceholder}
        onMouseDown={handleMouseDown}
      />
    </InputAdornment>
  );
};

export default NumberOperationValueInput;
export type { INumberOperationValueInputProps } from './types';

